import type { SpecType } from '@local/lib';

import { Button, Tooltip } from '@salesforce/design-system-react';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type { SpecDefinition, SpecState } from '../qualityFormulaTypes';

import { IconCategory } from '../../../shared/types/Enum';
import { SPEC_TYPES } from '../qualityFormulaMetadata';
import { getSpecValidationIssues } from '../qualityFormulaValidation';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { AddSpecMenu } from './AddSpecMenu';

// "Configured" reuses the exact same per-spec business-rule check "Test These Formulas" runs
// (getSpecValidationIssues) rather than a second, looser definition of "done" — a spec only reads
// as fully configured here once it would also pass a preview run.
const getSpecStatus = (specType: SpecType, state: SpecState): string =>
  getSpecValidationIssues(specType, state).length === 0 ? 'Configured' : 'Needs attention';

interface CompactAvatarProps {
  isSelected: boolean;
  onSelect: () => void;
  spec: SpecDefinition;
  state: SpecState;
}

const CompactAvatar: React.FC<CompactAvatarProps> = ({ isSelected, onSelect, spec, state }) => {
  const status = getSpecStatus(spec.key, state);

  return (
    <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-1)' }}>
      <Tooltip align="right" content={`${spec.label} — ${status}`}>
        <Button
          aria-current={isSelected ? 'true' : undefined}
          aria-label={`${spec.label}, ${status}`}
          className="qfw-compact-avatar"
          data-selected={isSelected}
          onClick={onSelect}
          variant="neutral"
        >
          {spec.label.charAt(0)}
        </Button>
      </Tooltip>
      <span className="qfw-nav-spec-label slds-text-body_small" style={{ textAlign: 'center' }}>
        {spec.label}
      </span>
    </div>
  );
};

/**
 * The spec picker/switcher standing in for the old quality-type filter + stacked card list — one
 * compact letter rail. The add menu manages adding and removing specs in both workbenches.
 */
export const SpecNavigator: React.FC = () => {
  const { addSpec, removeSpec, selectedSpecType, selectSpec, specStates } = useQualityFormulaStore(
    useShallow(store => ({
      addSpec: store.addSpec,
      removeSpec: store.removeSpec,
      selectedSpecType: store.selectedSpecType,
      selectSpec: store.selectSpec,
      specStates: store.specStates,
    })),
  );
  const isPlaygroundOpen = useQualityFormulaStore(store => store.isPlaygroundOpen);
  const setPlaygroundOpen = useQualityFormulaStore(store => store.setPlaygroundOpen);
  const isTemplateManagerOpen = useQualityFormulaStore(store => store.isTemplateManagerOpen);
  const setTemplateManagerOpen = useQualityFormulaStore(store => store.setTemplateManagerOpen);
  const showSpec = (specType: SpecType) => {
    selectSpec(specType);
    setPlaygroundOpen(false);
    setTemplateManagerOpen(false);
  };
  const addLineSpec = (specType: SpecType) => {
    addSpec(specType);
    setPlaygroundOpen(false);
    setTemplateManagerOpen(false);
  };

  const configuredSpecs = useMemo(() => SPEC_TYPES.filter(spec => specStates[spec.key].enabled), [specStates]);
  const availableSpecs = useMemo(() => SPEC_TYPES.filter(spec => !specStates[spec.key].enabled), [specStates]);

  return (
    <nav aria-label="Quality specifications" className="qfw-nav slds-box slds-theme_default" data-compact="true">
      <div className="qfw-nav-title">
        <span className="slds-text-title_caps slds-text-color_weak">Specs</span>
      </div>
      {configuredSpecs.map(spec => (
        <CompactAvatar
          isSelected={!isPlaygroundOpen && !isTemplateManagerOpen && selectedSpecType === spec.key}
          key={spec.key}
          onSelect={() => {
            showSpec(spec.key);
          }}
          spec={spec}
          state={specStates[spec.key]}
        />
      ))}
      <div className="qfw-nav-action-group">
        <span aria-hidden="true" className="qfw-nav-divider" />
        {/* `Popover` clones its child to inject the open-on-click handler, so keep the button as
            the direct child rather than wrapping it in a Tooltip. */}
        <AddSpecMenu
          availableSpecs={availableSpecs}
          onAdd={addLineSpec}
          onRemove={removeSpec}
          selectedSpecs={configuredSpecs}
        >
          <Button
            aria-label="Add a quality spec"
            className="qfw-compact-avatar qfw-compact-avatar-add"
            iconCategory={IconCategory.Utility}
            iconName="add"
            title="Add a quality spec"
            variant="icon"
          />
        </AddSpecMenu>
      </div>
      {configuredSpecs.length > 0 ? (
        <div className="qfw-nav-action-group">
          <span aria-hidden="true" className="qfw-nav-divider" />
          <div
            style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-1)' }}
          >
            <Tooltip align="right" content="Playground — Test Configured Formulas">
              <Button
                aria-current={isPlaygroundOpen ? 'true' : undefined}
                aria-label="Playground — test configured formulas"
                className="qfw-compact-avatar qfw-compact-avatar-playground"
                data-selected={isPlaygroundOpen}
                iconCategory={IconCategory.Utility}
                iconName="path_experiment"
                onClick={() => {
                  setPlaygroundOpen(true);
                }}
                title="Playground"
                variant="icon"
              />
            </Tooltip>
          </div>
        </div>
      ) : null}
      <div className="qfw-nav-action-group">
        <span aria-hidden="true" className="qfw-nav-divider" />
        <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', gap: 'var(--slds-g-spacing-1)' }}>
          <Tooltip align="right" content="Manage Templates — Create, Edit, or Delete Saved Templates">
            <Button
              aria-current={isTemplateManagerOpen ? 'true' : undefined}
              aria-label="Manage Templates — create, edit, or delete saved templates"
              className="qfw-compact-avatar qfw-compact-avatar-templates"
              data-selected={isTemplateManagerOpen}
              iconCategory={IconCategory.Utility}
              iconName="recipe"
              onClick={() => {
                setTemplateManagerOpen(true);
              }}
              title="Manage Templates"
              variant="icon"
            />
          </Tooltip>
        </div>
      </div>
    </nav>
  );
};
